<template>
  <div
    class="ms-input ms-editor w-100"
    :class="{ 'ms-validate': isValidate }"
  >
    <label
      v-if="label"
      class="label"
    >
      {{ label }}
      <span
        v-if="isRequired"
        class="required"
      />
    </label>
    <div
      v-tooltip="errorMessage"
      class="flex-row ms-input__inner--wrapper"
      :class="[
        { error: errorMessage, disabled: disabled },
        hasBorder ? 'border' : '',
      ]"
    >
      <span
        v-if="$slots.prefix || prefixIconClass"
        class="ms-input__prefix"
      >
        <span
          v-if="prefixIconClass"
          :class="prefixIconClass"
        />
        <slot name="prefix" />
      </span>
      <input
        v-if="trimModel"
        ref="input"
        v-model.trim="internalText"
        :title="internalText"
        class="ms-input-item flex w-100"
        :placeholder="placeholder"
        :disabled="disabled"
        :readOnly="readOnly"
        :maxlength="maxLength"
        :class="{ disabled: disabled }"
        v-on="listeners"
        :type="showPassword ? (passwordVisible ? 'text' : 'password') : type"
      >

      <input
        v-else
        ref="input"
        v-model="internalText"
        :title="internalText"
        class="ms-input-item flex w-100"
        :placeholder="placeholder"
        :disabled="disabled"
        :readOnly="readOnly"
        :maxlength="maxLength"
        :class="{ disabled: disabled }"
        v-on="listeners"
        :type="showPassword ? (passwordVisible ? 'text' : 'password') : type"
      >
      <span
        v-if="showPassword"
        class="ms-input__suffix"
      >
        <span
          :class="[passwordVisible ? 'eye-open' : 'eye-close', 'icon24-v2']"
          @click="handlePasswordVisible"
        />
      </span>
    </div>
    <div
      v-if="isShowErrorMessage && errorMessage"
      class="error-text"
      v-html="errorMessage"
    />
    <div
      v-if="$slots.messageCustom"
      class="ms-input__message-custom"
    >
      <slot name="messageCustom" />
    </div>
  </div>
</template>
<script>
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  watch,
  getCurrentInstance,
  nextTick,
} from 'vue'
import msBaseComponent from '@/components/base/MsBaseComponent.vue'
import { useValidateControl } from '@/setup/validateControl.js'
import { debounce } from 'lodash'
export default defineComponent({
  name: 'MsInput',
  extends: msBaseComponent,
  props: {
    /**
     * V-model bắn ra ngoài binding 2 chiều
     */
    modelValue: {
      type: [String, Number],
      default: null,
    },
    /**
     * Control có đang báo lỗi không
     */
    isError: {
      type: Boolean,
      default: false,
    },
    /**
     * Hiện thị chũ mờ ở dưới ô input
     */
    placeholder: {
      default: null,
      type: [Number, String],
    },
    // Giới hạn nhập của ô input
    maxLength: {
      default: null,
      type: Number,
    },
    // disabled
    disabled: {
      default: false,
      type: Boolean,
    },
    // Xác định xem input có border hay không?
    hasBorder: {
      default: true,
      type: Boolean,
    },
    /**
     * Trim giá trị model
     */
    trimModel: {
      default: false,
      type: Boolean,
    },

    /**
     * Show lên icon con mắt để ẩn hiện password
     */
    showPassword: {
      type: Boolean,
      default: false,
    },

    /**
     * Hiện đang phục vụ cho validate Require - message sẽ build format: title + không được để trống
     */
    title: {
      type: String,
      default: null,
    },

    /**
     * icon cho slot prefix
     */
    prefixIconClass: {
      type: String,
      default: null,
    },

    /**
     * Có validate khi blur ra ngoài không
     */
    validateBlur: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    'msKeydown',
    'update:modelValue',
    'update:isError',
    'focus',
    'blur',
    'change',
    'msKeyup',
  ],
  setup (props, { emit }) {
    // Variable
    const internalText = ref('')
    let focused = false
    let displayValue = null
    let scrollWidth = 0
    const input = ref(null)
    const {
      errorMessage,
      validate,
      isValidate,
      clearValidate,
      isRequired,
    } = useValidateControl({
      props,
    })

    const { proxy } = getCurrentInstance()
    // #region Input type Password

    const passwordVisible = ref(props.showPasswordState)

    const handlePasswordVisible = () => {
      passwordVisible.value = !passwordVisible.value

      nextTick(() => {
        input.value.focus()
      })
    }
    // #endregion

    // Method
    const hasTooltip = () => {
      if (scrollWidth > input?.value?.offsetWidth) {
        return true
      }
      return false
    }

    // Sự kiện focus vào ô input
    const onFocus = (e) => {
      focused = true
      emit('focus', internalText.value, e)
    }

    // Sự kiên blur ra ngoài ô input
    const onBlur = debounce((e) => {
      focused = false

      if (props.validateBlur) {
        validate()
      }
      emit('blur', internalText.value, e)
    }, 200)

    // if (errorMessage.value && typeof (e.relatedTarget?.getVueInstance) === 'function' && e.relatedTarget?.getVueInstance()?.$?.type?.name === 'MsButton' && e.relatedTarget?.getVueInstance()?.type === 'text') {
    //   e.relatedTarget.click()
    // }

    // Reset lại giá trị về ban đầu
    const reset = () => {
      internalText.value = ''
      displayValue = null
    }
    // Set giá trị cho ô input
    const setValue = (value) => {
      if (proxy.rules) {
        const rule = proxy.rules.find(
          (x) => x.name === 'pattern' && x.compareValue === 'phone'
        )
        if (rule && value) {
          value = value.replace(/ /g, '').replace(/\./g, '')
        }
      }
      internalText.value = value
    }

    // Lấy giá trị ô input
    const getValue = () => {
      return internalText.value
    }

    /**
     * Hủy lan rộng event
     * HTHIEP 09.02.2022
     */
    const cancelEvent = (e) => {
      if (e) {
        if (typeof e.preventDefault === 'function') {
          e.preventDefault()
        }

        if (typeof e.stopPropagation === 'function') {
          e.stopPropagation()
        }

        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation()
        }
      }
    }

    // Hooks
    // set internalText
    onMounted(() => {
      internalText.value = props.modelValue
    })

    /**
     * Lắng nghe event => thò ra ngoài
     * HTHIEP 11.02.2022: Làm mịn, migration sang vue 3
     */
    const listeners = computed(() => {
      return {
        blur: (e) => {
          cancelEvent(e)
          onBlur(e)
        },
        focus: (e) => {
          cancelEvent(e)
          onFocus(e)
        },
        change: (e) => {
          cancelEvent(e)
          emit('change', e, internalText.value)
        },
        keydown: (e) => {
          e.stopPropagation()
          emit(proxy.$ms.constant.CustomEvent.MsKeydown, e, internalText.value)
        },
        keyup: (e) => {
          emit('msKeyup', e, internalText.value)
        },
      }
    })

    /**
     * Lắng nghe xử lý update lại modelValue
     * HTHIEP 11.02.2022: Làm mịn, sửa lại từ vue 2 sang vue 3
     */
    watch(
      () => internalText.value,
      (value) => {
        if (value !== props.modelValue) {
          emit('update:modelValue', value)
          scrollWidth = input.value.scrollWidth
        }
      }
    )

    /**
     * Lắng nghe thay đổi modelValue set lại text cho input
     * HTHIEP 11.02.2022: Làm mịn, sửa lại từ vue 2 sang vue 3
     */
    watch(
      () => props.modelValue,
      (value) => {
        setValue(value)
      }
    )

    /**
     * cật nhật lại thông tin isError
     */
    watch(
      () => errorMessage.value,
      (value) => {
        emit('update:isError', !!value)
      }
    )
    return {
      internalText,
      focused,
      listeners,
      reset,
      hasTooltip,
      displayValue,
      input,
      getValue,
      errorMessage,
      validate,
      isValidate,
      clearValidate,
      isRequired,
      passwordVisible,
      handlePasswordVisible,
    }
  },
})
</script>
<style lang="scss" scoped>
@import '@/assets/scss/components/MsInput.scss';
</style>
